import React, {useEffect, useState} from 'react';
import Wrapper from '../../components/Wrapper';
import DashboardHeader from '../../components/DashboardHeader';
import './index.scss';
import Input from '../../components/Input';
import {
  useLazyGetFacilityQuery,
  useRemoveReportMutation,
  useLazyGetReportListQuery,
  useCheckReportMutation,
  useReportPdfLinkMutation,
} from '../../store/builder/inspections.api';
import {useDispatch} from 'react-redux';
import {setLoader} from '../../store/builder/reducer';
import ReportModal from '../../components/ReportModal';
import moment from 'moment';
import {Helmet} from 'react-helmet';
import {toast} from 'react-toastify';
import Datepicker from '../../components/Datepicker';
import Select from 'react-select';
import {useNavigate} from 'react-router-dom';
import {useDebounce} from '../LogSheets/hooks';
import useLoader from '../../global/UseLoadingHandler';
import GeneralModal from '../../components/GeneralModal/GeneralModal';
import Table from '../../components/Table/Table';
import {useTranslation} from '../../hooks/useTranslation';

const cellKeys = [
  'name',
  'updated_at',
  'facility',
  'year',
  'month_to',
  'created_by',
  'file_name',
];

export default function Report() {
  const {t} = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const urlParams = new URLSearchParams(window.location.search);
  const [reportModalStatus, setReportModalStatus] = useState(false);
  const [search, setSearch] = useState('');
  const [currentReport, setCurrentReport] = useState(null);
  const [dangerShow, setDangerShow] = useState(false);
  const [generateStatus, setGenerateStatus] = useState(false);
  const [facilityOptions, setFacilityOptions] = useState([]);
  const [reportFilters, setReportFilters] = useState({
    date_from: null,
    date_to: null,
    facility_id: null,
    equipment_id: null,
  });
  const [currentPage, setCurrentPage] = useState(1);

  const [
    getReportData,
    {data: reportData, isFetching: reportIsLoading, isSuccess: reportIsSuccess},
  ] = useLazyGetReportListQuery();

  const [getFacility, {data: facilityData}] = useLazyGetFacilityQuery();

  const [
    removeReport,
    {isFetching: removeReportIsLoading, isSuccess: removeReportIsSuccess},
  ] = useRemoveReportMutation();

  const [
    checkPdf,
    {
      isLoading: checkPdfIsLoading,
      isSuccess: checkPdfIsSuccess,
      error: checkPdfError,
    },
  ] = useCheckReportMutation();

  const [openLink, {isLoading: openLinkIsLoading}] = useReportPdfLinkMutation();
  useLoader([openLinkIsLoading, reportIsLoading]);
  const debouncedSearchValue = useDebounce(search, 500);

  const getHeaders = () => [
    t('report.tableHeaders.name'),
    t('report.tableHeaders.timestamp'),
    t('report.tableHeaders.facility'),
    t('report.tableHeaders.year'),
    t('report.tableHeaders.month'),
    t('report.tableHeaders.completedBy'),
    t('report.tableHeaders.download'),
    t('report.tableHeaders.action'),
  ];

  useEffect(() => {
    if (reportModalStatus || dangerShow) {
      document.body.style.overflow = 'hidden';
    } else {
      document.body.style.overflow = 'unset';
    }
  }, [reportModalStatus, dangerShow]);

  useEffect(() => {
    getFacility();
  }, []);

  useEffect(() => {
    if (facilityData) {
      setFacilityOptions(
        facilityData?.map(item => ({value: item.id, label: item.name})),
      );
    }
  }, [facilityData]);

  useEffect(() => {
    setReportFilters(old => ({
      ...old,
      facility_id: urlParams.get('facility_id'),
      date_from: urlParams.get('date_from'),
      date_to: urlParams.get('date_to'),
    }));
  }, []);

  useEffect(() => {
    getReportData({
      page: currentPage,
      facility_id: reportFilters?.facility_id,
      date_from: reportFilters?.date_from,
      date_to: reportFilters?.date_to,
      search: debouncedSearchValue,
      type: 'pdf',
    });
  }, [reportFilters, debouncedSearchValue, currentPage]);

  useEffect(() => {
    if (removeReportIsLoading || checkPdfIsLoading || openLinkIsLoading) {
      dispatch(setLoader(true));
    }
  }, [
    removeReportIsLoading,
    removeReportIsSuccess,
    checkPdfIsLoading,
    checkPdfIsSuccess,
  ]);

  useEffect(() => {
    if (checkPdfIsSuccess) {
      dispatch(setLoader(false));
      toast.success(t('report.messages.pdfRegenerated'));
    }
  }, [checkPdfIsSuccess]);

  useEffect(() => {
    if (checkPdfError) {
      dispatch(setLoader(false));
      toast.error(checkPdfError?.data?.message);
    }
  }, [checkPdfError]);

  useEffect(() => {
    if (removeReportIsSuccess) {
      dispatch(setLoader(false));
      toast.success(t('report.messages.reportDeleted'));
      setDangerShow(false);
    }
  }, [removeReportIsSuccess]);

  useEffect(() => {
    if (reportIsLoading) {
      dispatch(setLoader(true));
    }
    if (reportIsSuccess) {
      dispatch(setLoader(false));
    }
  }, [reportIsLoading, reportIsSuccess]);

  useEffect(() => {
    if (reportIsLoading) {
      dispatch(setLoader(true));
    } else {
      dispatch(setLoader(false));
    }
  }, [reportIsLoading]);

  useEffect(() => {
    const filteredArr = reportData?.data?.filter(el => el.file_name === 'wait');
    const interval = setInterval(() => {
      if (filteredArr?.length === 0) {
        clearInterval(interval);
      } else {
        getReportData({
          facility_id: reportFilters?.facility_id,
          date_from: reportFilters?.date_from,
          date_to: reportFilters?.date_to,
          search: debouncedSearchValue,
          type: 'pdf',
        });
      }
    }, 10000);
    return () => {
      clearInterval(interval);
    };
  }, [reportData]);

  const handleSearch = e => {
    setSearch(e.target.value);
    setCurrentPage(1);
  };
  const handleFilterDateFrom = date => {
    setReportFilters(old => ({
      ...old,
      date_from: date ? moment(date).format('YYYY-MM-DD') : null,
    }));
    setCurrentPage(1);
    navigate(
      `/report?${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${date ? `&date_from=${moment(date).format('YYYY-MM-DD')}` : ''}${urlParams.get('date_to') ? `&date_to=${urlParams.get('date_to')}` : ''}`,
    );
  };
  const handleFilterDateTo = date => {
    setReportFilters(old => ({
      ...old,
      date_to: date ? moment(date).format('YYYY-MM-DD') : null,
    }));
    setCurrentPage(1);
    navigate(
      `/report?${urlParams.get('facility_id') ? `&facility_id=${urlParams.get('facility_id')}` : ''}${urlParams.get('date_from') ? `&date_from=${urlParams.get('date_from')}` : ''}${date ? `&date_to=${moment(date).format('YYYY-MM-DD')}` : ''}`,
    );
  };

  const handlePageClick = page => {
    setCurrentPage(page.selected + 1);
  };

  return (
    <Wrapper>
      <Helmet>
        <title>{t('report.pageTitle')}</title>
      </Helmet>
      {reportModalStatus && (
        <ReportModal
          title={t('report.modals.createTitle')}
          exit={() => setReportModalStatus(false)}
          facility={facilityData?.map(item => ({
            value: item.id,
            label: item.name,
            default_type: item.default_type,
          }))}
          type={'report'}
        />
      )}
      {dangerShow && (
        <GeneralModal
          title={t('report.modals.deleteTitle')}
          onClose={() => setDangerShow(false)}
          onSubmit={() => removeReport(currentReport)}
          children={t('report.modals.deleteMessage')}
        />
      )}
      {generateStatus && (
        <GeneralModal
          title={t('report.modals.regenerateTitle')}
          onClose={() => setGenerateStatus(false)}
          onSubmit={() => {
            setGenerateStatus(false);
            dispatch(setLoader(true));
            checkPdf(currentReport);
          }}
          children={t('report.modals.regenerateMessage')}
        />
      )}
      <DashboardHeader
        title={t('report.title')}
        type={'report'}
        action={() => setReportModalStatus(true)}
      />
      <div className='report'>
        <div className='report__content'>
          <div className='report__search'>
            <Input
              type='text'
              name={'search'}
              id={'search'}
              onChange={handleSearch}
              image='/assets/icons/search.svg'
              placeholder={t('report.searchPlaceholder')}
              defaultValue={search}
              maxwidth={200}
            />
            <Datepicker
              type={'from'}
              setValue={handleFilterDateFrom}
              value={urlParams.get('date_from')}
            />
            <Datepicker
              type={'to'}
              setValue={handleFilterDateTo}
              value={urlParams.get('date_to')}
            />
            <Select
              styles={{
                control: (baseStyles, state) => ({
                  ...baseStyles,
                  borderColor: state.isFocused ? '#E9EAEB' : '#E9EAEB',
                  borderRadius: 8,
                  width: 200,
                  height: 42,
                  backgroundColor: '#F4F5F6',
                }),
              }}
              isClearable
              key={JSON.stringify(facilityOptions) + '1'}
              placeholder={t('report.filters.facility')}
              options={facilityOptions}
              defaultValue={
                facilityOptions?.filter(
                  item => item?.value === urlParams.get('facility_id'),
                )[0]
              }
              onChange={e => {
                setReportFilters(old => ({...old, facility_id: e?.value}));
                setCurrentPage(1);
                navigate(
                  `/report?${e?.value ? `&facility_id=${e?.value}` : ''}${urlParams.get('date_from') ? `&date_from=${urlParams.get('date_from')}` : ''}${urlParams.get('date_to') ? `&date_to=${urlParams.get('date_to')}` : ''}`,
                );
              }}
            />
          </div>
          {!!reportData?.data?.length && (
            <Table
              headers={getHeaders()}
              cellKeys={cellKeys}
              customRowType='reports_row'
              rows={reportData?.data}
              isSmallPaddings
              currentPage={currentPage}
              page={reportData?.pages}
              handlePageClick={handlePageClick}
              openLink={openLink}
              setDangerShow={setDangerShow}
              setCurrentReport={setCurrentReport}
              setGenerateStatus={setGenerateStatus}
              withPagination
            />
          )}
        </div>
      </div>
    </Wrapper>
  );
}
